import React, { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import useContentTag from 'hooks/Content/useContentTag'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { getContentTagIcon } from './contentTagIconUtils'
import MessageBanner, { type MessageBannerKind } from 'LuxKit/Banners/MessageBanner'
import type { ContentTagPage } from './contentTagTypes'

interface Props {
  slug: string
  tagName: string
  kind: MessageBannerKind
  page: ContentTagPage
}

/**
 * Renders a single ContentTag as a MessageBanner.
 */
function ContentTagBannerItem({ slug, tagName, kind, page }: Props) {
  const { contentTag } = useContentTag(slug)
  const history = useHistory()
  const location = useLocation()
  const isSearchPage = page === 'search'

  const isTagApplied = useMemo(() => {
    if (!isSearchPage) return false
    const currentTags = new URLSearchParams(location.search).get('offerTags')
    const tagsArray = currentTags ? currentTags.split(',').filter(Boolean) : []
    return tagsArray.includes(tagName)
  }, [isSearchPage, location.search, tagName])

  const icon = useMemo(() => {
    if (!contentTag) {
      return undefined
    }
    return getContentTagIcon(contentTag.fields.icon)
  }, [contentTag])

  // Search page: clicking the button adds the tag to URL search params
  const handleSearchButtonClick = useCallback(() => {
    if (!isSearchPage) return

    const currentSearch = location.search
    const currentTags = new URLSearchParams(currentSearch).get('offerTags')
    const tagsArray = currentTags ? currentTags.split(',').filter(Boolean) : []

    if (!tagsArray.includes(tagName)) {
      tagsArray.push(tagName)
    }

    const newSearch = setSearchParamValue(currentSearch, 'offerTags', tagsArray.join(','))
    history.push({
      ...location,
      search: newSearch,
    })
  }, [isSearchPage, tagName, history, location])

  const handleBackButtonClick = useCallback(() => {
    if (!isSearchPage) return

    const currentSearch = location.search
    const currentTags = new URLSearchParams(currentSearch).get('offerTags')
    const tagsArray = currentTags ? currentTags.split(',').filter(Boolean) : []

    const newTagsArray = tagsArray.filter(tag => tag !== tagName)

    const newSearch = newTagsArray.length > 0
      ? setSearchParamValue(currentSearch, 'offerTags', newTagsArray.join(','))
      : setSearchParamValue(currentSearch, 'offerTags', '')

    history.push({
      ...location,
      search: newSearch,
    })
  }, [isSearchPage, tagName, history, location])

  if (!contentTag) {
    return null
  }

  const showActionButtons = isSearchPage && contentTag.fields.linkText

  const primaryActionButtonProps = showActionButtons ? {
    children: isTagApplied ? 'Back' : contentTag.fields.linkText,
    onClick: isTagApplied ? handleBackButtonClick : handleSearchButtonClick,
  } : undefined

  return (
    <MessageBanner
      kind={kind}
      icon={icon}
      heading={contentTag.fields.title}
      primaryActionButtonProps={primaryActionButtonProps}
    >
      {contentTag.fields.subtitle}
    </MessageBanner>
  )
}

export default ContentTagBannerItem
